import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * The shop behind a register link (?ref=CODE), for "XY hat dir Schenkwerk empfohlen",
 * and the welcome offer: a lower fee for the first months after finishing onboarding.
 */
export interface Referrer {
  name: string;
  welcome_fee_rate: number;
  default_fee_rate: number;
  welcome_months: number;
}

/** A fee discount of the logged-in shop: its welcome bonus or the reward of a referral. */
export interface ReferralDiscount {
  kind: 'referrer' | 'welcome';
  points: number; // percentage points off the fee rate
  starts_on: string; // YYYY-MM-DD
  last_day: string; // YYYY-MM-DD, inclusive
  running: boolean;
  referred_name: string | null; // the recommended shop a reward is for
}

/**
 * A shop that registered through the logged-in shop's link. No sales figures: those
 * are the other shop's business, only whether it reached the qualifying sum.
 */
export interface ReferredShop {
  name: string | null; // only once it finished onboarding
  registered_on: string;
  status: 'registered' | 'selling' | 'rewarded' | 'not_rewarded';
  reward_starts_on: string | null;
  reward_last_day: string | null;
}

/** Everything the "Empfehlen" page shows, including the program's rules. */
export interface ReferralOverview {
  takes_part: boolean;
  link: string | null;
  fee_rate: number;
  default_fee_rate: number;
  discount_points: number;
  reward_months: number;
  welcome_months: number;
  max_running_discounts: number;
  qualifying_sales_cents: number;
  sale_hold_days: number; // a sale counts this long after it was made
  discounts: ReferralDiscount[];
  referrals: ReferredShop[];
}

/**
 * Shops recommending Schenkwerk. A register link's code names either a shop or an
 * affiliate; only a shop's code has a referrer here (an affiliate's answers 404).
 */
@Injectable({ providedIn: 'root' })
export class ReferralService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  /** The logged-in shop's link, fee discounts and recommended shops. */
  getOverview(): Observable<ReferralOverview> {
    return this.http.get<ReferralOverview>(`${this.apiUrl}/company/referrals`);
  }

  /** The recommending shop's name; errors (404) for any other code. */
  getReferrer(code: string): Observable<Referrer> {
    return this.http.get<Referrer>(`${this.apiUrl}/referrals/${encodeURIComponent(code)}`);
  }
}
